import { useState, useEffect, useMemo, useCallback } from "react";
import useAuth from "../../../../Hooks/useAuth";
import usePermission from "../../../../Hooks/usePermission";
import { useAlert } from "../../../../context/AlertContext";
import { FaBox, FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import {
  useAddProductInfoMutation,
  useDeleteProductInfoMutation,
  useFetchProductInfoQuery,
  useUpdateProductInfoMutation,
} from "../../../../store";
import { useFetchProductTypeQuery } from "../../../../store/storageRoles/productTypeApi";
import FormatDateHourString from "../../../../Components/Utils/FormatDateHourString";
import {
  TextInput,
  Pagination,
  CustomDropdown,
} from "../../../../Components/atoms";
import { ThreeDots } from "react-loader-spinner";
import PageShell from "../../../../Components/shared/PageShell";
import PageHeader from "../../../../Components/shared/PageHeader";
import SearchInput from "../../../../Components/shared/SearchInput";
import ListSkeleton from "../../../../Components/shared/ListSkeleton";
import EmptyState from "../../../../Components/shared/EmptyState";
import Modal from "../../../../Components/shared/Modal";

const ITEMS_PER_PAGE = 10;
const SKELETON_COLS = [
  { label: "Ürün Kodu", skeletonWidth: "w-20" },
  { label: "Ürün Adı", skeletonWidth: "w-32" },
  { label: "Ürün Tipi", skeletonWidth: "w-28" },
  { label: "Güncellenme", skeletonWidth: "w-24" },
  { label: "İşlemler", skeletonWidth: "w-16" },
];
const INITIAL_FORM = { productName: "", productCode: "", productTypeName: "" };

export default function ProductInfoPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const { showSuccess, showError } = useAlert();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("products.infoCreate");
  const canEdit = hasPermission("products.infoEdit");
  const canDelete = hasPermission("products.infoDelete");

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [selectedTypeId, setSelectedTypeId] = useState("");

  const ProductInfo = useFetchProductInfoQuery(token);
  const ProductTypes = useFetchProductTypeQuery(token);
  const [Update, resultUpdate] = useUpdateProductInfoMutation();
  const [Delete, resultDelete] = useDeleteProductInfoMutation();
  const [Add, resultAdd] = useAddProductInfoMutation();

  const products = useMemo(
    () => ProductInfo?.data?.data?.productInfoes || [],
    [ProductInfo?.data?.data?.productInfoes]
  );

  const productTypeOptions = useMemo(() => {
    const types = ProductTypes?.data?.data?.productTypes || [];
    return types.map((t) => ({ id: t._id, value: t._id, label: t.productTypeName }));
  }, [ProductTypes?.data?.data?.productTypes]);

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(
      (p) =>
        p.productName?.toLowerCase().includes(term) ||
        p.productCode?.toLowerCase().includes(term) ||
        p.productType?.productTypeName?.toLowerCase().includes(term)
    );
  }, [products, searchTerm]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);

  useEffect(() => setCurrentPage(1), [searchTerm]);

  const getErrMsg = useCallback(
    (err, fallback) => err?.data?.message || err?.error || fallback,
    []
  );

  const closeModals = useCallback(() => {
    setShowModal(false);
    setShowDeleteModal(false);
    setSelectedProduct(null);
    setFormData(INITIAL_FORM);
    setSelectedTypeId("");
  }, []);

  useEffect(() => {
    if (resultAdd.isSuccess) { showSuccess("Ürün eklendi!"); closeModals(); }
    else if (resultAdd.isError) showError(getErrMsg(resultAdd.error, "Ürün eklenemedi!"));
  }, [resultAdd.isSuccess, resultAdd.isError]);

  useEffect(() => {
    if (resultUpdate.isSuccess) { showSuccess("Ürün güncellendi!"); closeModals(); }
    else if (resultUpdate.isError) showError(getErrMsg(resultUpdate.error, "Güncelleme başarısız!"));
  }, [resultUpdate.isSuccess, resultUpdate.isError]);

  useEffect(() => {
    if (resultDelete.isSuccess) { showSuccess("Ürün silindi!"); closeModals(); }
    else if (resultDelete.isError) showError(getErrMsg(resultDelete.error, "Silme başarısız!"));
  }, [resultDelete.isSuccess, resultDelete.isError]);

  const handleInput = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleTypeChange = (opt) => {
    setSelectedTypeId(opt.value);
    setFormData((prev) => ({ ...prev, productTypeName: opt.label }));
  };

  const openAdd = () => { setSelectedProduct(null); setFormData(INITIAL_FORM); setSelectedTypeId(""); setShowModal(true); };
  const openEdit = (p) => {
    setSelectedProduct(p);
    setFormData({ productName: p.productName, productCode: p.productCode, productTypeName: p.productType?.productTypeName || "" });
    setSelectedTypeId(p.productType?._id || "");
    setShowModal(true);
  };
  const openDelete = (p) => { setSelectedProduct(p); setShowDeleteModal(true); };

  const handleSave = () => {
    if (selectedProduct) Update({ ...formData, id: selectedProduct._id, token });
    else Add({ ...formData, token });
  };

  const isLoading = ProductInfo.isFetching;

  return (
    <PageShell>
      <div className="p-3 sm:p-4 border-b border-gray-100 flex flex-col gap-3">
        <PageHeader
          icon={FaBox}
          iconColor="blue"
          title="Ürün Bilgileri"
          subtitle="Ürün listesini yönetin ve düzenleyin"
          stats={[{ label: "Toplam", value: products.length, color: "blue" }]}
          actions={canCreate ? [{ label: "Yeni Ürün", icon: FaPlus, onClick: openAdd, variant: "primary" }] : []}
          isLoading={isLoading}
        />
        <div className="flex items-center gap-2">
          <SearchInput
            placeholder="Ürün adı, kodu veya tipi ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            wrapperClassName="flex-1 max-w-md"
          />
          {canCreate && (
            <button onClick={openAdd} className="sm:hidden p-2.5 bg-blue-600 text-white rounded-xl shadow-sm">
              <FaPlus className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {isLoading ? (
          <ListSkeleton columns={SKELETON_COLS} rowCount={8} />
        ) : filteredProducts.length === 0 ? (
          <EmptyState
            icon={FaBox}
            message="Ürün bulunamadı"
            suggestion={searchTerm ? "Farklı bir arama terimi deneyin." : "Henüz ürün eklenmemiş."}
          />
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden p-3 space-y-2">
              {paginatedProducts.map((p) => (
                <div key={p._id} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{p.productName}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{p.productCode}</p>
                    </div>
                    {(canEdit || canDelete) && (
                      <div className="flex gap-1 shrink-0 ml-2">
                        {canEdit && <button onClick={() => openEdit(p)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><FaEdit className="w-3.5 h-3.5" /></button>}
                        {canDelete && <button onClick={() => openDelete(p)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><FaTrash className="w-3.5 h-3.5" /></button>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{p.productType?.productTypeName || "-"}</span>
                    <span className="text-[11px] text-gray-400">{FormatDateHourString(p.updatedInfo)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ürün Kodu</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ürün Adı</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ürün Tipi</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Güncellenme</th>
                    {(canEdit || canDelete) && <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">İşlemler</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedProducts.map((p) => (
                    <tr key={p._id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">{p.productCode}</td>
                      <td className="px-6 py-3 text-sm text-gray-900">{p.productName}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{p.productType?.productTypeName || "-"}</td>
                      <td className="px-6 py-3 text-sm text-gray-500">{FormatDateHourString(p.updatedInfo)}</td>
                      <td className="px-6 py-3 text-right">
                        {(canEdit || canDelete) && (
                          <div className="flex items-center justify-end gap-1">
                            {canEdit && <button onClick={() => openEdit(p)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><FaEdit className="w-4 h-4" /></button>}
                            {canDelete && <button onClick={() => openDelete(p)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><FaTrash className="w-4 h-4" /></button>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {!isLoading && filteredProducts.length > ITEMS_PER_PAGE && (
        <div className="border-t border-gray-100 p-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={filteredProducts.length}
            showInfo
          />
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal open={showModal} onClose={closeModals} maxWidth="md">
        <Modal.Header title={selectedProduct ? "Ürün Düzenle" : "Yeni Ürün Ekle"} onClose={closeModals} />
        <Modal.Body className="space-y-4">
          <TextInput label="Ürün Adı" value={formData.productName} onChange={(e) => handleInput("productName", e.target.value)} placeholder="Ürün adını girin" required />
          <TextInput label="Ürün Kodu" value={formData.productCode} onChange={(e) => handleInput("productCode", e.target.value)} placeholder="Ürün kodunu girin" required />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ürün Tipi</label>
            <CustomDropdown
              options={productTypeOptions}
              value={productTypeOptions.find((o) => o.value === selectedTypeId) || null}
              onChange={handleTypeChange}
              placeholder="Ürün tipi seçin"
              isDisabled={ProductTypes.isLoading}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-3">
          <button onClick={closeModals} className="px-4 py-2 text-gray-600 hover:text-gray-800">İptal</button>
          <button onClick={handleSave} disabled={resultAdd.isLoading || resultUpdate.isLoading} className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {(resultAdd.isLoading || resultUpdate.isLoading) ? <ThreeDots height={16} width={16} color="white" /> : selectedProduct ? "Güncelle" : "Ekle"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal open={showDeleteModal} onClose={closeModals} maxWidth="sm">
        <Modal.Header title="Ürün Sil" onClose={closeModals} />
        <Modal.Body>
          <p className="text-gray-600"><strong>{selectedProduct?.productName}</strong> adlı ürünü silmek istediğinize emin misiniz?</p>
          <p className="text-sm text-red-600 mt-2">Bu işlem geri alınamaz!</p>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-3">
          <button onClick={closeModals} className="px-4 py-2 text-gray-600 hover:text-gray-800">İptal</button>
          <button onClick={() => Delete({ id: selectedProduct._id, token })} disabled={resultDelete.isLoading} className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
            {resultDelete.isLoading ? <ThreeDots height={16} width={16} color="white" /> : "Sil"}
          </button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
}
